import { DateTimeParts, ExtractedMatch } from '../../../types';
import { FORMAT_PATTERNS } from '../../../inputPatterns/date/formatLabel';
import { DateFormatType } from '../../../inputPatterns/date/formats';
/**
 * Extracts year, month, day, and time components from a Moment.js object.
 *
 * @param {moment.Moment} value - A Moment.js object.
 * @returns {DateTimeParts} An object containing extracted date components.
 */
export const extractMomentDateTimeParts = (value: moment.Moment): DateTimeParts => ({
  year: value.year(),
  month: value.month(),
  day: value.date(),
  hours: value.hours(),
  minutes: value.minutes(),
  seconds: value.seconds(),
  milliseconds: value.milliseconds(),
});

/**
 * Extracts year, month, day, and time components from a Moment.js object in UTC.
 *
 * @param {moment.Moment} value - A Moment.js object.
 * @returns {DateTimeParts} An object containing extracted date components.
 */
export const extractMomentDateTimePartsInUTC = (value: moment.Moment): DateTimeParts => ({
  year: value.utc().year(),
  month: value.utc().month(),
  day: value.utc().date(),
  hours: value.utc().hours(),
  minutes: value.utc().minutes(),
  seconds: value.utc().seconds(),
  milliseconds: value.utc().milliseconds(),
});

/**
 * Extracts year, month, day, and time components from a Date object.
 *
 * @param {Date} date - The Date object to extract information from.
 * @returns {DateTimeParts} An object containing the extracted date components.
 */
export const extractDateTimeParts = (date: Date): DateTimeParts => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  day: date.getDate(),
  hours: date.getHours(),
  minutes: date.getMinutes(),
  seconds: date.getSeconds(),
  milliseconds: date.getMilliseconds(),
});

/**
 * Extracts year, month, day, and time components from a Date object in UTC.
 *
 * @param {Date} date - The Date object to extract information from.
 * @returns {DateTimeParts} An object containing the extracted date components.
 */
export const extractDateTimePartsInUTC = (date: Date): DateTimeParts => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth(),
  day: date.getUTCDate(),
  hours: date.getUTCHours(),
  minutes: date.getUTCMinutes(),
  seconds: date.getUTCSeconds(),
  milliseconds: date.getUTCMilliseconds(),
});
export const isMonthYearFormat = (format: string): boolean => {
  return [FORMAT_PATTERNS.MM_YYYY, FORMAT_PATTERNS.MMM_YYYY, FORMAT_PATTERNS.MMMM_YYYY].includes(format);
};

/**
 * Attempts to identify the best matching date substring from the input string
 * using a list of supported date format regex patterns.
 *
 * Each provided regex pattern is executed against the input. If a match is found
 * with named capture groups, the result is collected. Among all successful matches,
 * the one with the longest match length is selected and returned.
 *
 * This function ensures that when multiple regex patterns could partially match
 * overlapping parts of the input (e.g. "06/15/2024" matching both MM/YYYY and MM/DD/YYYY),
 * the most specific and precise match is returned — based on character length.
 *
 *
 * @param {string} input - The raw user-input string to scan for date patterns.
 * @param {DateFormatType[]} formats - A list of date format definitions.
 *
 * @returns {ExtractedMatch | undefined} - The best matching result, including the matched
 *          string, extracted groups, and format label. Returns undefined if there are no matches.
 */
export function getBestDateMatch(input: string, formats: DateFormatType[]): ExtractedMatch | undefined {
  return formats
    .map(({ label, regex }) => {
      const match = regex.exec(input);
      if (match?.groups) {
        return {
          match: match[0].trim(),
          matchedGroups: { ...match.groups }, // spread to avoid non-enumerable properties from RegExp
          label,
        };
      }
      return null;
    })
    .filter((res): res is ExtractedMatch => res !== null)
    .sort((a, b) => b.match.length - a.match.length)[0]; // longest match wins
}
